import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getFirebaseErrorMessage } from '../utils/authUtils';
import { useCooldown } from './useCooldown';

/** Client-side throttle on the "Resend link" button. */
export const VERIFY_RESEND_COOLDOWN_SECONDS = 30;

const SEND_FAILED = "We couldn't send the link. Try again in a moment.";

const sendFailureMessage = (err: any): string =>
    (err?.code ? getFirebaseErrorMessage(err.code, SEND_FAILED) : err?.message) || SEND_FAILED;

/**
 * How the person reached "check your inbox":
 * sent       → a link just went out (resend starts on cooldown)
 * unverified → sign-in bounced as unverified; a fresh link is being sent
 * failed     → the account exists but its link could not be requested
 */
export type VerifyEmailArrival = 'sent' | 'unverified' | 'failed';

interface UseVerifyEmailStepOptions {
    email: string;
    password: string;
    /** Where the login page shows its one inline error ('' clears it). */
    setError: (message: string) => void;
}

/**
 * The login page's `verify-email` step. The backend sends the link (the
 * AuthContext asks it to); this owns the resend: its cooldown, the
 * confirmation line, and whether any link has gone out at all, so the card
 * never says "We sent" after a request that failed.
 */
export const useVerifyEmailStep = ({ email, password, setError }: UseVerifyEmailStepOptions) => {
    const { resendVerificationEmail } = useAuth();
    const cooldown = useCooldown();
    const [sending, setSending] = useState(false);
    const [notice, setNotice] = useState('');
    const [linkFailed, setLinkFailed] = useState(false);

    const enter = (arrival: VerifyEmailArrival) => {
        setNotice('');
        setLinkFailed(arrival === 'failed');
        cooldown.start(arrival === 'sent' ? VERIFY_RESEND_COOLDOWN_SECONDS : 0);
    };

    /** One link request; the resend cooldown runs from each one that goes out. */
    const sendLink = async () => {
        setSending(true);
        try {
            await resendVerificationEmail(email, password);
            setLinkFailed(false);
            cooldown.start(VERIFY_RESEND_COOLDOWN_SECONDS);
        } finally {
            setSending(false);
        }
    };

    const handleResend = async () => {
        if (cooldown.remaining > 0 || sending) return;
        setError('');
        setNotice('');
        try {
            await sendLink();
            setNotice('Verification link sent.');
        } catch (err: any) {
            setError(sendFailureMessage(err));
        }
    };

    /**
     * A password sign-in refused as unverified: open the step and send a fresh
     * link straight away, so "we sent a link" is true now rather than about a
     * link that may be long gone. If it fails, the step says no link went out
     * and Resend stays available.
     */
    const enterAndSend = async () => {
        enter('unverified');
        try {
            await sendLink();
        } catch (err: any) {
            setLinkFailed(true);
            setError(sendFailureMessage(err));
        }
    };

    const clear = () => {
        cooldown.clear();
        setNotice('');
        setLinkFailed(false);
    };

    return {
        sending,
        resendCooldown: cooldown.remaining,
        notice,
        linkFailed,
        enter,
        enterAndSend,
        handleResend,
        clear,
    };
};
